/**
 * Product Support Sidebar Component
 *
 * Displays Pharma product details and guidelines in a stacked sidebar layout.
 */

import { Timer, Dna, UserRoundCheck } from 'lucide-react-native';
import { Linking } from 'react-native';

import { primum_default_avatar } from '@/assets/images';
import { DisclaimerText } from '@/components/MedicalOnDemand/DisclaimerText';
import { ProductDetailTab } from '@/components/MedicalOnDemand/ProductDetailTab';
import { ProductSupportBadge } from '@/components/MedicalOnDemand/ProductSupportBadge';
import { ProductTeamDetails } from '@/components/MedicalOnDemand/ProductTeamDetails';
import { Avatar, AvatarImage } from '@/components/ui/avatar';
import { Divider } from '@/components/ui/divider';
import { Heading } from '@/components/ui/heading';
import { HStack } from '@/components/ui/hstack';
import { Icon } from '@/components/ui/icon';
import { LinkText } from '@/components/ui/link';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';
import {
  PharmaProductSupportTeam,
  getProductData,
  type Product,
  type ProductId,
} from '@/constants/medicalOnDemand';
import { useExpertAvatar } from '@/hooks/useExpertAvatar';
import { useModTeamName } from '@/hooks/useModTeamName';

type ProductSupportSidebarProps = {
  pharmaProductSupportTeam: PharmaProductSupportTeam;
  selectedProductIds?: ProductId[];
  focusedProductId?: ProductId | null;
  onFocusedProductChange?: (productId: ProductId) => void;
};

/**
 * Sidebar displaying Pharma product information and guidelines in a stacked layout.
 */
export const ProductSupportSidebar = ({
  pharmaProductSupportTeam,
  selectedProductIds,
  focusedProductId,
  onFocusedProductChange,
}: ProductSupportSidebarProps) => {
  const selectedProducts = (selectedProductIds ?? [])
    .map((id) => getProductData(id))
    .filter((product): product is Product => !!product);

  const focusedSelectedProduct =
    selectedProducts.find((product) => product.id === focusedProductId) ??
    selectedProducts[0] ??
    null;

  const expertAvatarUrl = useExpertAvatar(pharmaProductSupportTeam.expertId);

  const teamName = useModTeamName(pharmaProductSupportTeam.name);

  return (
    <VStack className="rounded-lg border border-outline-100">
      {/* Header with Pharma Logo */}
      <VStack className="bg-background-50 px-5 py-6">
        <HStack className="items-center gap-6">
          <Avatar size="xl">
            <AvatarImage
              source={expertAvatarUrl ? { uri: expertAvatarUrl } : primum_default_avatar}
              alt={teamName + ' avatar'}
            />
          </Avatar>

          <VStack className="flex-1 gap-2">
            <Heading size="lg" className="text-typography-900">
              {pharmaProductSupportTeam.specialty}
            </Heading>
            <HStack className="flex-wrap items-center gap-2 py-2">
              <ProductSupportBadge size="md" />
              <Text className="text-xs text-typography-700">{teamName}</Text>
            </HStack>
          </VStack>
        </HStack>

        <Divider className="my-4" />

        <VStack className="gap-3">
          {focusedSelectedProduct ? (
            <HStack className="items-start gap-4">
              {selectedProducts.map((product) => (
                <VStack key={product.id} className="min-w-0 flex-1 gap-2">
                  <Text className="font-medium text-base leading-6 text-typography-900">
                    {product.brandName} ({product.genericName})
                  </Text>
                  <HStack className="items-start gap-2">
                    <Icon as={UserRoundCheck} size="sm" className="mt-0.5 text-typography-600" />
                    <VStack className="flex-1 gap-1">
                      <Text className="text-base text-typography-800">{product.indication}</Text>
                      {product.headerBullets?.map((bullet) => (
                        <HStack key={bullet} className="items-start gap-1">
                          <Text className="text-base text-typography-800">•</Text>
                          <Text className="flex-1 text-base text-typography-800">{bullet}</Text>
                        </HStack>
                      ))}
                    </VStack>
                  </HStack>
                  <HStack className="items-start gap-2">
                    <Icon as={Dna} size="sm" className="mt-0.5 text-typography-600" />
                    <Text className="flex-1 text-base text-typography-800">
                      {product.drugTarget ? `${product.drugTarget}-directed ` : ''}
                      {product.drugClass}
                    </Text>
                  </HStack>
                </VStack>
              ))}
            </HStack>
          ) : (
            <HStack className="items-center gap-2 py-2">
              <Icon as={Dna} size="sm" className="text-typography-600" />
              <Text className="text-sm text-typography-800">
                Treatments for: <Text className="font-semibold text-sm">MM</Text>
              </Text>
            </HStack>
          )}

          <HStack className="items-center gap-2 rounded-md bg-background-0 px-3 py-2">
            <Icon as={Timer} size="sm" className="text-typography-600" />
            <Text className="text-xs text-typography-600">~1 business day response time</Text>
          </HStack>
        </VStack>
      </VStack>

      <Divider />

      <VStack className="px-5 py-6">
        <VStack className="gap-6">
          <ProductTeamDetails
            pharmaProductSupportTeam={pharmaProductSupportTeam}
            selectedProduct={null}
          />

          {selectedProducts.length > 0 ? (
            <>
              <VStack className="gap-6">
                <Text className="font-bold text-base text-primary-500">
                  {`${selectedProducts.some((product) => product.indicationLabel === 'Indications') ? 'Indications' : 'Indication'} and Usage`}
                </Text>
                {selectedProducts.map((product) => (
                  <VStack key={product.id} className="gap-3">
                    <ProductDetailTab
                      product={product}
                      variant="stacked"
                      showStackedHeading={false}
                    />
                    <DisclaimerText>{product.disclaimer}</DisclaimerText>
                    <LinkText
                      size="xs"
                      className="font-medium text-primary-500 underline"
                      onPress={() => {
                        Linking.openURL(product.prescribingInfoUrl).catch(() => {});
                      }}>
                      View full Prescribing Information
                    </LinkText>
                  </VStack>
                ))}
              </VStack>
            </>
          ) : null}
        </VStack>
      </VStack>
    </VStack>
  );
};
